import { useState, useRef, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";

import "../styles/projLoginPage.css";
import { API_BASE } from "../config";
import ServerCaptchaWidget from "./ServerCaptchaWidget";
import { getSession, setSession } from "../session";
import { setAccessToken } from "../apiFetch";

export default function LoginPage() {
  useEffect(() => { document.title = "Sign In — Owllet Reading Quest"; }, []);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formLoadedAt, setFormLoadedAt] = useState(null);

  const captchaRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { setFormLoadedAt(Date.now()); }, []);

  const handleGoogleSuccess = async (tokenResponse) => {
    setIsLoading(true);
    setErrors({});
    try {
      const res = await fetch(`${API_BASE}/auth/google`, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify({ accessToken: tokenResponse.access_token }),
      });
      const data = await res.json();
      if (data.isNewUser) {
        navigate("/registrationConfirmed", { state: { username: data.username } });
      } else if (data.success) {
        if (data.accessToken) setAccessToken(data.accessToken);
        setSession(data.userId, data.username, data.ageGroup, data.owlletId, data.isTeacher, data.displayName || null);
        if (data.isAdmin) localStorage.setItem('isAdmin', '1');
        if (data.difficulty_tier != null) localStorage.setItem('difficulty_tier', String(data.difficulty_tier));
        else localStorage.removeItem('difficulty_tier');
        setShowSuccess(true);
        setTimeout(() => navigate("/home"), 1200);
      } else {
        setErrors({ general: data.message || "Google sign-in failed" });
      }
    } catch {
      setErrors({ general: "Something went wrong. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () =>
      setErrors({ general: "Google sign-in was cancelled or failed" }),
  });

  if (getSession()) {
    return <Navigate to="/home" replace />;
  }

  const validate = () => {
    const errs = {};
    if (!username.trim()) errs.username = "Username is required";
    if (!password.trim()) errs.password = "Password is required";
    if (!captchaOk) errs.captcha = "Please complete the security check";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const resetCaptcha = () => {
    captchaRef.current?.refresh();
    setCaptchaOk(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const { token: captchaToken, answer: captchaAnswer } = captchaRef.current?.getChallenge() ?? {};
      const res = await fetch(`${API_BASE}/login`, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify({ username, password, captchaToken, captchaAnswer, website: honeypot, formLoadedAt }),
      });
      const data = await res.json();

      if (data.success) {
        if (data.accessToken) setAccessToken(data.accessToken);
        setSession(data.userId, data.username, data.ageGroup, data.owlletId, data.isTeacher, data.displayName || null);
        if (data.isAdmin) localStorage.setItem('isAdmin', '1');
        if (data.difficulty_tier != null) localStorage.setItem('difficulty_tier', String(data.difficulty_tier));
        else localStorage.removeItem('difficulty_tier');
        setShowSuccess(true);
        setTimeout(() => navigate("/home"), 1200);
      } else {
        setErrors({ general: data.message || "Invalid username or password" });
        resetCaptcha();
      }
    } catch {
      setErrors({ general: "Something went wrong. Please try again." });
      resetCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="lp-container">
        <div className="lp-card lp-success-card">
          <div className="lp-success-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="lp-success-title">Login Successful!</h2>
          <p className="lp-success-msg">Redirecting you to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lp-container">
      <div className="lp-card">
        {/* Branding */}
        <div className="lp-brand">
          <div className="lp-logo">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <h1 className="lp-title">Reading Quest</h1>
          <p className="lp-subtitle">Sign in to continue your adventure</p>
        </div>

        {/* Form */}
        <form className="lp-form" onSubmit={handleSubmit} noValidate>
          {errors.general && (
            <div className="lp-error-banner">
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                className="lp-error-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
              {errors.general}
            </div>
          )}

          {/* Username */}
          <div className="lp-field">
            <label htmlFor="username">Username</label>
            <div
              className={`lp-input-wrap ${errors.username ? "lp-input-err" : ""}`}
            >
              <svg
                className="lp-field-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </div>
            {errors.username && (
              <p className="lp-err-text">{errors.username}</p>
            )}
          </div>

          {/* Password */}
          <div className="lp-field">
            <label htmlFor="password">Password</label>
            <div
              className={`lp-input-wrap ${errors.password ? "lp-input-err" : ""}`}
            >
              <svg
                className="lp-field-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="lp-eye-btn"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <p className="lp-err-text">{errors.password}</p>
            )}
          </div>

          {/* Forgot password */}
          <div className="fp-forgot-link">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>

          {/* Honeypot — hidden from real users, bots fill it in */}
          <div className="contact-hp-field" aria-hidden="true">
            <label htmlFor="lp-website">Website</label>
            <input
              id="lp-website"
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Server-side CAPTCHA */}
          <div className="lp-field">
            <ServerCaptchaWidget
              ref={captchaRef}
              onVerify={(ok) => setCaptchaOk(ok)}
            />
            {errors.captcha && <p className="lp-err-text">{errors.captcha}</p>}
          </div>

          {/* Submit */}
          <button type="submit" disabled={isLoading} className="lp-submit">
            {isLoading ? (
              <>
                <svg className="lp-spinner" fill="none" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="lp-spinner-track"
                  />
                  <path
                    fill="currentColor"
                    className="lp-spinner-fill"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Signing in…
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="lp-divider">
          <span>or</span>
        </div>

        {/* Google Sign-In */}
        <button
          type="button"
          className="lp-google-btn"
          onClick={() => signInWithGoogle()}
          disabled={isLoading}
        >
          <svg className="lp-google-icon" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>

        <p className="fp-back-link" style={{ marginTop: "1.25rem" }}>
          Don't have an account? <Link to="/register">Create one</Link>
        </p>
      </div>
    </div>
  );
}
